import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Text,
    Accordion,
    AccordionItem,
    AccordionPanel,
    AccordionButton,
    useMultiStyleConfig,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/* Components */
import Link from '@salesforce/retail-react-app/app/components/link'
import {Link as RouterLink} from 'react-router-dom'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

/* Icons */
import {ChevronDownType2} from '../../icons/index'

/* Utils */
import {
    resolveGDIStyles,
    getGDIHideStyles,
    hasGDIViewTypes,
    parseGDIViewTypes,
    GDI_TEXT_BLOCK_FIELD_MAP
} from '../../optimizely/gdi/utils/gdi-styles'
import {resolveGDILink} from '../../optimizely/gdi/utils/gdi-link-resolver'
import {
    STYLE_VARIANT_MAP,
    extraStylePropsFor,
    isTextLinkStyle,
    getMegaMenuLinkSx
} from '../../optimizely/gdi/utils/gdi-cta-style'
import {sendClickEvent} from '../../../utils/gtm-utils'


const CTA_STYLE_MAP = {
    Color: 'color',
    BackgroundColor: 'backgroundColor',
    BorderColor: 'borderColor',
    MinWidth: 'minWidth',
    GDICtaBlockPadding: 'padding',
    Margin: 'margin'
}

const isDesktopHoverEntry = (entry) => parseGDIViewTypes(entry?.ViewTypesToApply).includes('_hover')

const getPrimaryCtaStyleEntry = (stylesArr) => {
    if (!Array.isArray(stylesArr)) return {}
    return (
        stylesArr.find((entry) => !hasGDIViewTypes(entry) && entry?.Style) ||
        stylesArr.find((entry) => !isDesktopHoverEntry(entry) && entry?.Style) ||
        stylesArr.find((entry) => entry?.Style) ||
        {}
    )
}

/* Anchors are inline by default — vertical padding/margin authored in CMS would
 * not visually push siblings. Force inline-block so authored spacing renders. */
const LINK_BOX_DEFAULTS = {display: 'inline-block'}

/* When rendering footer CTAs as Chakra Buttons (variant branch), Chakra's Button
 * already manages display/sizing. We only need to ensure the button doesn't
 * stretch to fill its container and that the variant's own typography/decoration
 * isn't overridden by inherited anchor styling. `textDecoration: 'none'` is a
 * base-state override only — `_hover` is intentionally omitted here so the
 * variant's hover (e.g. webPrimarySmall background change) stays intact. */
const BUTTON_LAYOUT_DEFAULTS = {
    width: 'auto',
    alignSelf: 'flex-start',
    textDecoration: 'none'
}

import {openClaraChat} from '../../../hooks/use-clara-chat'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'

const AccordionIcon = ({isExpanded}) => {
    const styles = useMultiStyleConfig('FooterNavigationLinks', {isExpanded})

    return <ChevronDownType2 {...styles.accordionIcon} />
}

// GTM Modification
const sendGTMEvent = (type) => {
    const normalized = type.toLowerCase().replace(/\s+/g, '-')
    sendClickEvent(`footer_link_${normalized}`)
}

AccordionIcon.propTypes = {
    isExpanded: PropTypes.bool
}

const NavigationLinks = ({footerData, ...otherProps}) => {
    const styles = useMultiStyleConfig('FooterNavigationLinks')
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id

    /* Map CMS data to footer links structure */
    const NAV_LINKS_DATA = useMemo(() => {
        if (!footerData?.listOfLinks?.length) {
            return []
        }

        // Map CMS data to the expected structure
        return footerData.listOfLinks.map((column, index) => {
            const titleStyles = resolveGDIStyles(column.columnTitleStyle, GDI_TEXT_BLOCK_FIELD_MAP).styles
            const itemStyles = resolveGDIStyles(column.columnLinksStyle, GDI_TEXT_BLOCK_FIELD_MAP).styles
            return {
                sectionId: `section-${index}`,
                title: column.columnTitle || '',
                titleStyles,
                itemStyles,
                links: (column.columnLinks || [])
                    .map((ctaBlock) => {
                        // Respect HideBlockOn — same logic as gdiCtaBlock
                        const hideStyles = getGDIHideStyles(ctaBlock.HideBlockOn)
                        if (hideStyles.doNotLoadComponent) return null

                        const ctaStylesList = ctaBlock.GDICtaBlockStylesPerViewType
                        const style = getPrimaryCtaStyleEntry(ctaStylesList)
                        const label = ctaStylesList?.find?.((entry) => entry?.CtaText)?.CtaText || ''
                        const linkData = resolveGDILink(
                            ctaBlock.GDICtaBlockLink?.[0]?.ContentLink?.Expanded
                        )

                        /* Route the authored Style through the same map GDICtaBlock uses.
                         * Tertiary/text styles (e.g. "Underlined on Hover") keep the
                         * existing plain-link rendering; everything else renders as a
                         * Chakra Button with the matching design-system variant so the
                         * footer matches the brand's button treatment. */
                        const styleName = style.Style
                        const variant = isTextLinkStyle(styleName)
                            ? null
                            : STYLE_VARIANT_MAP[styleName] || null
                        const extraStyleProps = variant ? extraStylePropsFor(styleName) : null
                        const styleSx =
                            variant && styleName === 'Mega Menu Link' ? getMegaMenuLinkSx() : null

                        // Check if this is a Store Locator link and if it should be shown
                        const isStoreLocator =
                            label.toLowerCase().includes('store') &&
                            label.toLowerCase().includes('locator')

                        if (isStoreLocator && getSiteEnvVarValue(config.app, 'STORELOCATOR_ENABLED', siteId) !== 'true') {
                            return null
                        }

                        // Check if this is a Cookie Preferences link
                        const isCookiePreferences =
                            label.toLowerCase().includes('cookie') &&
                            label.toLowerCase().includes('preference')

                        // Language-agnostic: a CTA block with no GDICtaBlockLink is a
                        // special-action link (Clara chat trigger). Avoids label matching
                        // which breaks for non-English locales.
                        // Secondary check: label contains 'chat' as a fallback for edge cases.
                        const isChatWithUs =
                            !ctaBlock.GDICtaBlockLink?.length ||
                            label.toLowerCase().includes('chat')
                        return {
                            label,
                            href: linkData?.href || (isCookiePreferences || isChatWithUs ? '#' : null),
                            isExternalUrl: !!linkData?.isExternal,
                            ...(linkData?.target && {target: linkData.target}),
                            ...(linkData?.rel && {rel: linkData.rel}),
                            id: label, // Use label as ID for GTM tracking
                            isChatWithUs,
                            sx: hideStyles.sx,
                            preventDefault: isCookiePreferences,
                            variant,
                            extraStyleProps,
                            styleSx,
                            ctaStyles: resolveGDIStyles(
                                ctaBlock.GDICtaBlockStylesPerViewType,
                                CTA_STYLE_MAP
                            ).styles,
                            onClick: isCookiePreferences
                                ? function () {
                                      if (typeof window !== 'undefined' && window.Didomi) {
                                          window.Didomi.preferences.show()
                                      }
                                  }
                                : null
                        }
                    })
                    .filter((link) => link && link.href)
            }
        })
    }, [footerData, siteId])

    /* Single rendering path for footer links. When the CMS-authored Style maps
     * to a design-system button variant (e.g. "Button Solid" → webPrimarySmall),
     * render as a Chakra Button polymorphically (`as={Link/ChakraLink}`) so the
     * brand's button treatment is applied while navigation still flows through
     * react-router for internal hrefs. Tertiary/text styles keep the existing
     * inline-link rendering. */
    const renderLink = (link, section) => {
        const dataTestId = `footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`
        const handleClick = (e) => {
            if (link.isChatWithUs) {
                openClaraChat(e)
                if (link.id) {
                    sendGTMEvent(link.id)
                }
                return
            }
            if (link.preventDefault) {
                e.preventDefault()
                if (link.onClick) {
                    link.onClick()
                }
            }
            if (link.id) {
                sendGTMEvent(link.id)
            }
        }
        const sx = {...LINK_BOX_DEFAULTS, ...section.itemStyles, ...link.ctaStyles}

        if (link.variant) {
            /* Button branch: skip LINK_BOX_DEFAULTS (its inline-block conflicts with
             * Chakra Button's inline-flex layout) and skip styles.footerLink (theme
             * footer-link text styling does not apply to a real button). Only the
             * style-specific pseudo sx (e.g. Mega Menu underline animation) and the
             * CMS-authored ctaStyles are layered on top of the variant. */
            const buttonSx = {...(link.styleSx || {}), ...section.itemStyles, ...link.ctaStyles}
            const buttonProps = {
                'data-testid': dataTestId,
                variant: link.variant,
                ...BUTTON_LAYOUT_DEFAULTS,
                ...(link.extraStyleProps || {}),
                sx: buttonSx,
                onClick: handleClick
            }
            return link.isExternalUrl ? (
                <Button
                    as={ChakraLink}
                    href={link.href}
                    isExternal={link.target === '_blank'}
                    {...(link.target && {target: link.target})}
                    {...(link.rel && {rel: link.rel})}
                    {...buttonProps}
                >
                    {link.label}
                </Button>
            ) : (
                <Button as={RouterLink} to={link.href} {...buttonProps}>
                    {link.label}
                </Button>
            )
        }

        return link.isExternalUrl ? (
            <ChakraLink
                data-testid={dataTestId}
                href={link.href}
                isExternal={link.target === '_blank'}
                {...(link.target && {target: link.target})}
                {...(link.rel && {rel: link.rel})}
                {...styles.footerLink}
                sx={sx}
                onClick={handleClick}
            >
                {link.label}
            </ChakraLink>
        ) : (
            <Link
                data-testid={dataTestId}
                to={link.href}
                {...styles.footerLink}
                sx={sx}
                onClick={handleClick}
            >
                {link.label}
            </Link>
        )
    }

    return (
        <React.Fragment {...otherProps}>
            <HideOnDesktop as={Accordion} allowMultiple>
                {NAV_LINKS_DATA.map((section) => (
                    <AccordionItem key={section.sectionId} {...styles.accordionItem}>
                        {({isExpanded}) => (
                            <>
                                <AccordionButton
                                    data-testid={`footer-accordion-button-${section.sectionId}`}
                                    {...styles.accordionButton}
                                >
                                    <Text {...styles.sectionTitle} sx={section.titleStyles || {}}>
                                        {section.title}
                                    </Text>
                                    <AccordionIcon isExpanded={isExpanded} />
                                </AccordionButton>
                                <AccordionPanel
                                    {...(isExpanded
                                        ? styles.accordionPanelExpanded
                                        : styles.accordionPanel)}
                                >
                                    {section.links.map((link) => (
                                        <Box key={link.label} {...styles.linkContainer} {...(link.sx && {sx: link.sx})}>
                                            {renderLink(link, section)}
                                        </Box>
                                    ))}
                                </AccordionPanel>
                            </>
                        )}
                    </AccordionItem>
                ))}
            </HideOnDesktop>
            {NAV_LINKS_DATA.map((section) => (
                <HideOnMobile key={section.sectionId}>
                    <Box>
                        <Text {...styles.sectionTitle} sx={section.titleStyles || {}}>
                            {section.title}
                        </Text>
                        {section.links.map((link) => (
                            <Box key={link.label} {...styles.linkContainer} {...(link.sx && {sx: link.sx})}>
                                {renderLink(link, section)}
                            </Box>
                        ))}
                    </Box>
                </HideOnMobile>
            ))}
        </React.Fragment>
    )
}

NavigationLinks.propTypes = {
    footerData: PropTypes.object,
    otherProps: PropTypes.object
}

export default NavigationLinks
